import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import toast from 'react-hot-toast'
import Drawer from '../../components/common/Drawer'
import Label from '../../components/form/Label'
import Input from '../../components/form/input/InputField'
import Switch from '../../components/form/switch/Switch'
import { api, apiHeaders } from '../../configs/api'
import { useAuth } from '../../context/AuthContext'
import { useProduct } from '../../context/ProductContext'

type ProductFormValues = { name: string }

const ProductForm = ({
    type,
    open,
    handleClose
}: {
    type: 'create' | 'edit'
    open: boolean
    handleClose: () => void
}) => {
    const { unity, token } = useAuth()
    const { detail, getProduct, loadProducts } = useProduct()
    const [isDefault, setIsDefault] = useState(false)
    const form = useForm<ProductFormValues>({ mode: 'onChange' })
    const formRef = useRef<HTMLFormElement>(null)
    const navigate = useNavigate()

    const handleSave = async (data: ProductFormValues) => {
        if (!unity || !token) return
        if (type === 'edit' && !detail?.product.id) return toast.error('Nenhum produto selecionado!')

        try {
            const response = await fetch(`${api}product/${type === 'create' ? 'create' : 'update'}`, {
                method: 'POST',
                headers: apiHeaders(token),
                body: JSON.stringify({
                    ...data,
                    id: type === 'edit' ? detail?.product.id : undefined,
                    image: type === 'edit' ? detail?.product.image : null,
                    isDefault,
                    unityID: unity.unityID
                })
            })
            const result = await response.json()
            if (!response.ok || !result.success || !result.product?.id) throw new Error(result.message)

            await loadProducts()
            await getProduct(result.product.id)
            handleClose()
            toast.success(result.message || 'Produto salvo com sucesso!')
            if (type === 'create') navigate(`/produto/${result.product.id}`)
        } catch (error) {
            toast.error(error instanceof Error && error.message ? error.message : 'Erro ao salvar produto!')
        }
    }

    useEffect(() => {
        if (type === 'edit' && detail) {
            form.reset({ name: detail.product.name || '' })
            setIsDefault(detail.product.isDefault)
        } else if (type === 'create') {
            form.reset({ name: '' })
            setIsDefault(false)
        }
    }, [detail, form, open, type])

    return (
        <Drawer
            open={open}
            handleClose={handleClose}
            title={type === 'create' ? 'Adicionar produto' : 'Editar produto'}
            handleSave={() => formRef.current?.requestSubmit()}
        >
            <form ref={formRef} onSubmit={form.handleSubmit(handleSave)}>
                <div className='space-y-5'>
                    <div>
                        <Label required>Nome</Label>
                        <Input
                            {...form.register('name', { required: true })}
                            placeholder='Nome do produto'
                            error={Boolean(form.formState.errors.name)}
                        />
                    </div>
                    <div>
                        <Label help='Ao ativar, o produto padrão anterior desta unidade será desmarcado.'>
                            Filtro inicial de negócios
                        </Label>
                        <Switch label='Produto padrão' checked={isDefault} onChange={setIsDefault} />
                    </div>
                </div>
            </form>
        </Drawer>
    )
}

export default ProductForm
